import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTeachingBlockStore } from '../../TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTableType } from '../graphql/types';
import { createTeachingBlockVersionTableColumns } from '../utils/TeachingBlockVersionTableUtils';

type TeachingBlockVersionSubjectsTableProps = {
  versionUuid: string | undefined;
  search: string;
  versionSubjectUuids: string[];
  onRowSelection: (rowData: TeachingBlockVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  onRemove: (uuids: string[]) => void | Promise<void>;
};

export const TeachingBlockVersionSubjectsTable = observer(
  ({
    versionUuid,
    search,
    versionSubjectUuids,
    onRowSelection,
    onShowLessonClick,
    onRemove,
  }: TeachingBlockVersionSubjectsTableProps) => {
    const { t } = useTranslation();

    const { sorting, saveSorting } = useColumnsSort('teachingBlockVersionSubjectsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTeachingBlockVersionTableColumns(t('subject.title', { count: 1 })), [t]);

    const store = useTeachingBlockStore();
    const { confirm, ConfirmationDialog } = useConfirm();

    // Fächer, die noch auf einer (platzierten oder freien) Karte liegen, dürfen nicht entfernt werden.
    const usedSubjects = useMemo(
      () => [
        ...store.placedCards.flatMap((c) => (c.subject?.uuid ? [c.subject.uuid] : [])),
        ...store.freeCards.flatMap((c) => (c.subject?.uuid ? [c.subject.uuid] : [])),
      ],
      [store.placedCards, store.freeCards],
    );

    const handleRemove = useCallback(
      async (uuids: string[]) => {
        const res = await confirm({
          title: t('timetableVersion.removeSubjects', { count: uuids.length }),
          message: t('timetableVersion.removeSubjectsDescription', { count: uuids.length }),
        });
        if (res) {
          await onRemove(uuids);
        }
      },
      [confirm, onRemove, t],
    );

    const createActionItems = useCallback(
      (row: Row<TeachingBlockVersionTableType>): DropdownMenuItem[] => {
        return [
          {
            label: t('teachingBlockVersion.editData'),
          },
          {
            label: t('teachingBlockVersion.test'),
          },
          {
            label: t('teachingBlockVersion.remove'),
            type: 'default',
            color: 'error',
            disabled: store.readonly || usedSubjects.includes(row.original.uuid),
            onClick: async () => {
              await handleRemove([row.original.uuid]);
            },
          },
        ];
      },
      [handleRemove, t, store.readonly, usedSubjects],
    );

    const subjectTableData: TeachingBlockVersionTableType[] = [];
    store.subjects.forEach((s) => {
      const { html, label } = hexToColorOption(s.timetableConfig?.color ?? '');
      subjectTableData.push({
        color: {
          color: html,
          colorLabel: label,
        },
        count: 0,
        name: s.name,
        shortName: s.shortName,
        uuid: s.uuid,
        epochEnabled: s.epochEnabled,
      });
    });

    return (
      <>
        <Table<TeachingBlockVersionTableType>
          globalFilter={search}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={subjectTableData}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('teachingBlockVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  disabled={store.readonly}
                  trigger={
                    <Button
                      disabled={store.readonly}
                      hierarchy='secondary'
                      icon={<DotsHorizontalIcon className='small' />}
                    />
                  }
                >
                  <DropdownMenu data={createActionItems(row)} />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(_event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
